import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchTreks } from '../services/trekService';
import { endUserTiming, startUserTiming } from '../utils/userTiming';

const scheduleBackgroundTask = (task) => {
    if (typeof window !== 'undefined') {
        if ('requestIdleCallback' in window) {
            const id = window.requestIdleCallback(task, { timeout: 1200 });
            return () => window.cancelIdleCallback(id);
        }
        if ('requestAnimationFrame' in window) {
            const id = window.requestAnimationFrame(task);
            return () => window.cancelAnimationFrame(id);
        }
    }

    const timeoutId = setTimeout(task, 120);
    return () => clearTimeout(timeoutId);
};

const useFetchTreks = ({ defer = false, enabled = true } = {}) => {
    const query = useQuery({
        queryKey: ['treks'],
        queryFn: async () => {
            startUserTiming('treks_fetch_start');
            const treks = await fetchTreks();
            startUserTiming('treks_fetch_end');
            endUserTiming({
                measureName: 'treks_fetch_duration',
                startMark: 'treks_fetch_start',
                endMark: 'treks_fetch_end',
            });
            return treks;
        },
        enabled: enabled && !defer,
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
    });

    const { data, error, fetchStatus, isFetching, isLoading, refetch } = query;

    useEffect(() => {
        if (!enabled || !defer || data || fetchStatus !== 'idle') return undefined;
        return scheduleBackgroundTask(() => refetch());
    }, [data, defer, enabled, fetchStatus, refetch]);

    return {
        treks: data || [],
        loading: isLoading || isFetching,
        error,
    };
};

export default useFetchTreks;
